import {AppealStatus} from 'types/complaints';
import {DeclarationStatus} from 'types/declarations';

import {ActiveStatuses, GtinStatuses} from 'types/gtins';
import {RemarkingReason} from 'types/products-detail';

import {
    AuthenticStatus,
    CheckStatus,
    DocumentStatus,
    GdsStatus,
    OrderStatus,
    ProductStatus,
} from '../dictionaries';

export const STATUS_COLOR_LIST = {
    GRAY: '#848484',
    GRAY_ALT: '#607C90',
    GREEN: '#4ABD74',
    GREEN_ALT: '#55CC52',
    RED: '#F54849',
    ORANGE: '#FF9500',
    BLUE: '#6466E9',
    YELLOW: '#C5A400',
    TEAL: '#2AD0BF',
};

export const REMARKING_REASON_COLORS: Record<
    RemarkingReason,
    {background: string; text: string}
> = {
    DAMAGED: {background: '#FFF3B6', text: '#C5A400'},
    LOST: {background: '#FFCCB6', text: '#C51700'},
    MISPRINT: {background: '#D5ECFF', text: '#0069C5'},
    OTHER: {background: '#ECECEC', text: '#737373'},
};

export const APPEAL_STATUS_COLORS: Record<AppealStatus, string> = {
    [AppealStatus.SUBMITTED]: STATUS_COLOR_LIST.BLUE,
    [AppealStatus.IN_PROGRESS]: STATUS_COLOR_LIST.ORANGE,
    [AppealStatus.PROCESSED]: STATUS_COLOR_LIST.GREEN,
    [AppealStatus.RESOLVED]: STATUS_COLOR_LIST.GREEN,
    [AppealStatus.REJECTED]: STATUS_COLOR_LIST.RED,
};

export const PRODUCT_STATUS_COLORS: {DEFAULT: string} & Partial<Record<ProductStatus, string>> = {
    [ProductStatus.GENERATED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [ProductStatus.ISSUED]: STATUS_COLOR_LIST['YELLOW'],
    [ProductStatus.APPLIED]: STATUS_COLOR_LIST['ORANGE'],
    [ProductStatus.INTRODUCED]: STATUS_COLOR_LIST['BLUE'],
    [ProductStatus.SOLD]: STATUS_COLOR_LIST['GRAY'],
    [ProductStatus.DROPOUT]: STATUS_COLOR_LIST['GRAY_ALT'],
    [ProductStatus.AGGREGATED]: STATUS_COLOR_LIST['GREEN'],
    [ProductStatus.DISAGGREGATED]: STATUS_COLOR_LIST['GRAY'],
    [ProductStatus.ANNULLED]: STATUS_COLOR_LIST['RED'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const RECEIPT_STATUS_COLORS = {
    [CheckStatus.NEW]: STATUS_COLOR_LIST['BLUE'],
    [CheckStatus.CLOSED]: STATUS_COLOR_LIST['RED'],
    [CheckStatus.DECLINED]: STATUS_COLOR_LIST['ORANGE'],
    [CheckStatus.APPROVED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [CheckStatus.CREATED]: STATUS_COLOR_LIST['GRAY_ALT'],
    [CheckStatus.PROCESSED]: STATUS_COLOR_LIST['GREEN'],

    [CheckStatus.UNDER_APPROVAL]: STATUS_COLOR_LIST['TEAL'],
    [CheckStatus.RETURNED]: STATUS_COLOR_LIST['BLUE'],
    [CheckStatus.PENDING]: STATUS_COLOR_LIST['GRAY'],
    [CheckStatus.APPROVED_FOR_SHIPMENT]: STATUS_COLOR_LIST['YELLOW'],
    [CheckStatus.CHECK_SUCCESS]: STATUS_COLOR_LIST['GREEN'],
    [CheckStatus.CHECK_FAIL]: STATUS_COLOR_LIST['RED'],
    [CheckStatus.ACCEPTED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [CheckStatus.REJECTED]: STATUS_COLOR_LIST['RED'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const DOCUMENT_STATUS_COLORS = {
    [DocumentStatus.PROCESSING]: STATUS_COLOR_LIST['GRAY_ALT'],
    [DocumentStatus.DECLINED]: STATUS_COLOR_LIST['RED'],
    [DocumentStatus.CREATED]: STATUS_COLOR_LIST['GRAY_ALT'],
    [DocumentStatus.APPROVED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [DocumentStatus.PROCESSED]: STATUS_COLOR_LIST['GREEN'],
    [DocumentStatus.DRAFT]: STATUS_COLOR_LIST['GRAY_ALT'],
    [DocumentStatus.IN_PROGRESS]: STATUS_COLOR_LIST['GRAY_ALT'],
    [DocumentStatus.SUBMITTED]: STATUS_COLOR_LIST['BLUE'],
    [DocumentStatus.COMPLETED]: STATUS_COLOR_LIST['GREEN_ALT'],

    [CheckStatus.NEW]: STATUS_COLOR_LIST['BLUE'],
    [CheckStatus.UNDER_APPROVAL]: STATUS_COLOR_LIST['TEAL'],
    [CheckStatus.RETURNED]: STATUS_COLOR_LIST['BLUE'],
    [CheckStatus.PENDING]: STATUS_COLOR_LIST['GRAY'],
    [CheckStatus.APPROVED_FOR_SHIPMENT]: STATUS_COLOR_LIST['YELLOW'],
    [CheckStatus.CHECK_SUCCESS]: STATUS_COLOR_LIST['GREEN'],
    [CheckStatus.CHECK_FAIL]: STATUS_COLOR_LIST['RED'],
    [CheckStatus.ACCEPTED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [CheckStatus.REJECTED]: STATUS_COLOR_LIST['ORANGE'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const ORDER_STATUS_COLORS = {
    [OrderStatus.DECLINED]: STATUS_COLOR_LIST['RED'],
    [OrderStatus.CLOSED]: STATUS_COLOR_LIST['GRAY'],
    [OrderStatus.APPROVED]: STATUS_COLOR_LIST['BLUE'],
    [OrderStatus.READY]: STATUS_COLOR_LIST['GREEN'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const DECLARATIONS_STATUS_COLORS: {DEFAULT: string} & Record<DeclarationStatus, string> = {
    DRAFT: STATUS_COLOR_LIST['GRAY'],
    REJECTED: STATUS_COLOR_LIST['RED'],
    REVIEW: STATUS_COLOR_LIST['YELLOW'],
    SUBMITTED: STATUS_COLOR_LIST['BLUE'],
    CONFIRMED: STATUS_COLOR_LIST['GREEN'],
    REJECTED_AUTOMATIC: STATUS_COLOR_LIST['ORANGE'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const PRODUCT_GTIN_STATUS_COLORS: Record<GtinStatuses | 'DEFAULT', string> = {
    [GtinStatuses.PUBLISHED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [GtinStatuses.PENDING]: STATUS_COLOR_LIST['ORANGE'],
    [GtinStatuses.DRAFT]: STATUS_COLOR_LIST['BLUE'],
    [GtinStatuses.BLOCKED]: STATUS_COLOR_LIST['GRAY_ALT'],
    [GtinStatuses.REJECTED]: STATUS_COLOR_LIST['RED'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const PRODUCT_ACTIVE_STATUS_COLORS = {
    [ActiveStatuses.ACTIVE]: STATUS_COLOR_LIST['GREEN_ALT'],
    [ActiveStatuses.INACTIVE]: STATUS_COLOR_LIST['RED'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const GDS_STATUS_COLORS = {
    [GdsStatus.DRAFT]: STATUS_COLOR_LIST['GRAY_ALT'],
    [GdsStatus.SUBMITTED]: STATUS_COLOR_LIST['BLUE'],
    [GdsStatus.REVIEW]: STATUS_COLOR_LIST['YELLOW'],
    [GdsStatus.CONFIRMED]: STATUS_COLOR_LIST['GREEN_ALT'],
    [GdsStatus.REJECTED]: STATUS_COLOR_LIST['RED'],
    [GdsStatus.REJECTED_AUTOMATIC]: STATUS_COLOR_LIST['ORANGE'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};

export const AUTHENTIC_STATUS_COLORS = {
    [AuthenticStatus.AUTHENTIC]: STATUS_COLOR_LIST['GREEN'],
    [AuthenticStatus.COUNTERFEIT]: STATUS_COLOR_LIST['RED'],
    DEFAULT: STATUS_COLOR_LIST['GRAY'],
};
